import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { motion } from 'framer-motion';
import { HiBookOpen, HiArrowNarrowLeft, HiCalendar, HiTag } from 'react-icons/hi';

interface BlogPostItem {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  cover_image_url: string | null;
  tags: string[] | null;
  created_at: string;
}

export default function LatestBlogSection() {
  const [posts, setPosts] = useState<BlogPostItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLatestPosts = async () => {
      try {
        const { data, error } = await supabase
          .from('blog_posts')
          .select('id, slug, title, excerpt, cover_image_url, tags, created_at')
          .eq('is_published', true)
          .order('created_at', { ascending: false })
          .limit(3);

        if (error) throw error;
        setPosts(data || []);
      } catch (err) {
        console.error('Error fetching latest blog posts for home:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchLatestPosts();
  }, []);

  return (
    <section className="py-16 md:py-24 bg-black relative z-10 border-t border-white/5">
      <div className="container mx-auto px-4 md:px-8">
        {/* هدر بخش */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10 md:mb-12">
          <div>
            <div className="flex items-center gap-2 text-primary font-bold text-xs md:text-sm mb-2">
              <HiBookOpen className="w-4 h-4" />
              <span>تحریریه و مجله تخصصی گیمینگ</span>
            </div>
            <h2 className="text-2xl md:text-4xl font-black text-white tracking-tight">
              جدیدترین مقالات و اخبار بلاگ
            </h2>
          </div>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-primary hover:text-white font-bold text-sm group transition-colors"
          >
            <span>ورود به بلاگ و آرشیو مقالات</span>
            <HiArrowNarrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          </Link>
        </div>

        {/* لیست مقالات */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map((n) => (
              <div key={n} className="h-80 rounded-2xl bg-white/5 animate-pulse border border-white/10" />
            ))}
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-12 rounded-2xl bg-white/5 border border-white/10">
            <p className="text-gray-400 text-sm">مقاله‌ای در دسترس نیست.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {posts.map((post) => (
              <motion.article
                key={post.id}
                whileHover={{ y: -6 }}
                transition={{ duration: 0.2 }}
                className="group bg-zinc-900/60 rounded-3xl overflow-hidden border border-white/10 hover:border-primary/40 shadow-xl transition-all flex flex-col"
              >
                {/* کاور مقاله */}
                <Link to={`/blog/${post.slug}`} className="block relative aspect-video w-full overflow-hidden bg-zinc-950">
                  <img
                    src={post.cover_image_url || 'https://images.unsplash.com/photo-1542751371-adc38448a05e?q=80&w=1200&auto=format&fit=crop'}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => {
                      e.currentTarget.src = '/AE logo.svg';
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-transparent to-transparent opacity-60" />
                </Link>

                {/* محتوای کارت */}
                <div className="p-6 flex-1 flex flex-col justify-between">
                  <div>
                    {/* تاریخ انتشار */}
                    <div className="flex items-center gap-2 text-gray-500 text-[11px] mb-3">
                      <HiCalendar className="w-3.5 h-3.5 text-primary" />
                      <span>
                        {new Date(post.created_at).toLocaleDateString('fa-IR', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </span>
                    </div>

                    <Link to={`/blog/${post.slug}`}>
                      <h3 className="text-lg md:text-xl font-bold text-white group-hover:text-primary transition-colors line-clamp-2 mb-3 leading-snug">
                        {post.title}
                      </h3>
                    </Link>

                    <p className="text-gray-400 text-xs md:text-sm line-clamp-3 leading-relaxed mb-4">
                      {post.excerpt}
                    </p>
                  </div>

                  <div>
                    {/* تگ‌ها */}
                    {post.tags && post.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-5 pt-3 border-t border-white/5">
                        {post.tags.slice(0, 3).map((tag) => (
                          <span
                            key={tag}
                            className="inline-flex items-center gap-1 text-[10px] bg-white/5 text-gray-300 px-2 py-0.5 rounded-md border border-white/5"
                          >
                            <HiTag className="w-2.5 h-2.5 text-primary" />
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}

                    <Link
                      to={`/blog/${post.slug}`}
                      className="inline-flex items-center gap-2 text-primary group-hover:text-white text-xs md:text-sm font-bold transition-colors"
                    >
                      <span>مطالعه مقاله</span>
                      <HiArrowNarrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
